import { useState, useMemo } from 'react';
import { Search, ChevronRight } from 'lucide-react';
import { useData } from '../../contexts/DataContext';
import { colors, Header, Card, PillToggle, StatusBadge } from '../shared';

const OpenWOsScreen = ({ onBack }) => {
  const {
    getCurrentTech,
    getOpenWOsForTech,
    getRecentLaborWOsForTech,
  } = useData();

  const [tab, setTab] = useState('open');
  const [search, setSearch] = useState('');
  const [selectedWO, setSelectedWO] = useState(null);

  const currentTech = getCurrentTech();
  const openWOs = getOpenWOsForTech();
  const recentWOs = getRecentLaborWOsForTech(null, 14);

  const activeList = tab === 'open' ? openWOs : recentWOs;

  const filteredWOs = useMemo(() => {
    if (!search.trim()) return activeList;
    const q = search.toLowerCase();
    return activeList.filter(wo =>
      (wo.id || '').toLowerCase().includes(q) ||
      (wo.woNumber || '').toLowerCase().includes(q) ||
      (wo.description || '').toLowerCase().includes(q) ||
      (wo.property || '').toLowerCase().includes(q) ||
      (wo.unit || '').toLowerCase().includes(q)
    );
  }, [activeList, search]);

  if (selectedWO) {
    return (
      <div className="h-full overflow-y-auto pb-20" style={{ background: colors.black }}>
        <Header
          title={selectedWO.woNumber || selectedWO.id}
          subtitle={selectedWO.status}
          showBack
          onBack={() => setSelectedWO(null)}
        />
        <div className="px-4 space-y-4">
          <Card>
            <div className="space-y-3">
              <div>
                <p className="text-xs mb-1" style={{ color: colors.textMuted }}>Description</p>
                <p className="font-medium" style={{ color: colors.textPrimary }}>
                  {selectedWO.description || 'No description'}
                </p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-xs mb-1" style={{ color: colors.textMuted }}>Property</p>
                  <p className="text-sm" style={{ color: colors.textSecondary }}>
                    {selectedWO.property || '-'}
                  </p>
                </div>
                <div>
                  <p className="text-xs mb-1" style={{ color: colors.textMuted }}>Unit</p>
                  <p className="text-sm" style={{ color: colors.textSecondary }}>
                    {selectedWO.unit || '-'}
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-xs mb-1" style={{ color: colors.textMuted }}>Status</p>
                  <StatusBadge status={selectedWO.status}>{selectedWO.status}</StatusBadge>
                </div>
                <div>
                  <p className="text-xs mb-1" style={{ color: colors.textMuted }}>Created</p>
                  <p className="text-sm" style={{ color: colors.textSecondary }}>
                    {selectedWO.createdDate
                      ? new Date(selectedWO.createdDate).toLocaleDateString()
                      : '-'}
                  </p>
                </div>
              </div>
              {selectedWO.tech && (
                <div>
                  <p className="text-xs mb-1" style={{ color: colors.textMuted }}>Assigned To</p>
                  <p className="text-sm" style={{ color: colors.textSecondary }}>{selectedWO.tech}</p>
                </div>
              )}
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto pb-20" style={{ background: colors.black }}>
      <Header
        title="Work Orders"
        subtitle={`${currentTech?.name || 'Tech'}`}
        showBack
        onBack={onBack}
      />

      <div className="px-4">
        <PillToggle
          options={[
            { value: 'open', label: `Open (${openWOs.length})` },
            { value: 'recent', label: 'Last 14d' },
          ]}
          value={tab}
          onChange={setTab}
        />

        {/* Search */}
        <div className="relative mt-3 mb-4">
          <Search
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2"
            style={{ color: colors.textMuted }}
          />
          <input
            type="text"
            placeholder="Search WO ID, description, property..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-3 rounded-xl outline-none text-sm"
            style={{
              background: colors.inputBg,
              color: colors.textPrimary,
              border: `1px solid ${colors.border}`,
            }}
          />
        </div>

        {/* WO List */}
        {filteredWOs.length === 0 ? (
          <div className="text-center py-12">
            <p className="font-medium" style={{ color: colors.textSecondary }}>
              {search ? 'No matching work orders' : tab === 'open' ? 'No open work orders' : 'No recent work orders'}
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {filteredWOs.map(wo => (
              <Card
                key={wo.id}
                onClick={() => setSelectedWO(wo)}
                className="flex items-center justify-between"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-mono text-sm font-semibold" style={{ color: colors.yellow }}>
                      {wo.woNumber || wo.id}
                    </span>
                    <StatusBadge status={wo.status}>{wo.status}</StatusBadge>
                  </div>
                  <p className="text-sm truncate" style={{ color: colors.textPrimary }}>
                    {wo.description || 'No description'}
                  </p>
                  <p className="text-xs mt-1" style={{ color: colors.textMuted }}>
                    {wo.property}{wo.unit ? ` #${wo.unit}` : ''}
                    {wo.createdDate ? ` \u00b7 ${new Date(wo.createdDate).toLocaleDateString()}` : ''}
                  </p>
                </div>
                <ChevronRight size={18} style={{ color: colors.textMuted }} />
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default OpenWOsScreen;
